import { useState, useRef, useEffect, useCallback } from "react";
import cls from "classnames";

import * as Posthog from "@qoohoo/common/utils/analytics/posthog";
import useCouponState from "store/couponState";

import BackChevronIcon from "./icons/BackChevronIcon";
import CrossIcon from "./icons/CrossIcon";
import DiscountIcon from "./icons/DiscountIcon";
import validateCoupon from "utils/validateCoupon";
import Spinner from "../../../common/library/loader/Spinner";
import InputField from "../../../common/components/library/Form/InputField";
import usePreferencesStore from "store/preferencesStore";

const CouponField = (props) => {
  const coupon = useCouponState((state) => state.coupon?.data);
  const [isEditable, setIsEditable] = useState(false);

  if (coupon) {
    return <CouponApplied coupon={coupon} />;
  } else if (isEditable) {
    return <CouponInput setIsEditable={setIsEditable} />;
  } else {
    return <CouponEmpty setIsEditable={setIsEditable} />;
  }
};

function CouponEmpty(props) {
  const { setIsEditable } = props;
  const preferences = usePreferencesStore((state) => state.preferences.data);

  const onClick = () => setIsEditable(true);

  return (
    <div
      className="relative w-full h-11 flex bg-primary-light gap-2 px-3 rounded-[0.25rem] cursor-pointer items-center"
      onClick={onClick}
    >
      <DiscountIcon className="h-5 w-5" fill={preferences.theme.brand_color} />
      <div className="flex flex-col flex-1 min-w-0 text-left">
        <span className="font-bold text-sm text-primary">Have a coupon?</span>
      </div>
      <div className="flex-shrink-0">
        <div className="rotate-180">
          <BackChevronIcon className="w-5 h-5" fill={preferences.theme.brand_color} />
        </div>
      </div>
    </div>
  );
}

function CouponInput(props) {
  const [coupon, setCoupon] = useState("");
  const isLoading = useCouponState((state) => state.isLoading);
  
  const preferences = usePreferencesStore((state) => state.preferences.data);

  const trackAnalytics = () => {
    const data = {
      current_screen: "checkout_view",
      current_route: window.location.pathname,
      // payment_gateway: "razorpay",
      coupon_code: coupon,
      platform: "web",
    };

    console.log("Posthog button click data: apply_coupon_button", data);

    Posthog.buttonClick("apply_coupon_button", data);
  };

  const onSubmit = async () => {
    trackAnalytics();
    await validateCoupon(coupon);
  };

  const handleInputChange = (e) => {
    setCoupon(e.target.value.trim());
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onSubmit();
    }
  };
  return (
    <>
      <InputField
        className="bg-primary-light !border-none"
        inputClass="text-[#16191b] font-semibold placeholder-[rgba(22,25,27,.3)]"
        value={coupon}
        placeholder="Enter coupon code"
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        autoFocus
        addonBefore={() => (
          <div className="pl-3">
            <DiscountIcon className="h-5 w-5" fill={preferences.theme.brand_color} />
          </div>
        )}
        addonAfter={() => {
          return (
            <div className="pr-3">
              {isLoading ? (
                <Spinner color={preferences.theme.brand_color} className="!h-5 !w-5 border" />
              ) : (
                <button
                  className={cls(
                    "font-bold text-sm text-primary cursor-pointer disabled:cursor-not-allowed",
                    !coupon && "text-opacity-40"
                  )}
                  disabled={!coupon}
                  onClick={onSubmit}
                >
                  APPLY
                </button>
              )}
            </div>
          );
        }}
      />
    </>
  );
}

function CouponApplied(props) {
  const { coupon } = props;
  const setCoupon = useCouponState((state) => state.setCoupon);

  const [lottie, setLottie] = useState(null);

  const lottieContainer = useRef(null);

  const clearCoupon = () => {
    setCoupon(null);
  };

  useEffect(() => {
    import("lottie-web").then((Lottie) => setLottie(Lottie.default));
  }, []);

  const playLottie = useCallback(() => {
    lottie && lottie.loadAnimation({
      container: lottieContainer.current,
      path: "/lottie/confetti.json",
      autoplay: true,
      loop: false,
      renderer: "svg",
    });
  }, [lottie]);

  useEffect(() => {
    if (lottie && lottieContainer.current) {
      playLottie();
    }

    return () => lottie && lottie.destroy();
  }, [lottie, playLottie]);

  return (
    <div className="relative h-fit w-full flex bg-[#E7FFEC] gap-2 p-3 rounded-[0.25rem]">
      <div
        className="absolute left-0 right-0 self-center z-50 pointer-events-none scale-[150%]"
        ref={lottieContainer}
      ></div>      
      <DiscountIcon className="h-5 w-5" fill="#4E985E" />
      <div className="flex flex-col flex-1 min-w-0 text-left">
        <span className="font-extrabold text-base text-[#4E985E] uppercase leading-[1.375rem]">
          {coupon.code}
        </span>
        <span className="mt-1 text-sm leading-5 text-[#4E985E]">
          {coupon.applyText}
        </span>
      </div>
      <div className="flex-shrink-0">
        <div className="cursor-pointer" onClick={clearCoupon}>
          <CrossIcon fill="#4E985E" />
        </div>
      </div>
    </div>
  );
}

export default CouponField;
